/**
 * Reviews TanStack Query hooks.
 *
 * All GET endpoints for the Reviews API.
 * Uses useApiQuery from dashboard-shared for consistent defaults.
 * All query keys use the reviewsKeys factory for reliable cache invalidation.
 */

import { useApiQuery } from "@plerity/dashboard-shared/hooks";
import type {
  BusinessesResponse,
  CompetitorComparison,
  CompetitorsResponse,
  LocationsResponse,
  Review,
  ReviewAlertsResponse,
  ReviewCampaign,
  ReviewCampaignsResponse,
  ReviewSettings,
  ReviewStats,
  ReviewsListResponse,
  ReviewTrends,
} from "../types/reviews";
import { reviewsKeys } from "./keys";
import {
  AlertsGuard,
  asResponseGuard,
  BusinessesGuard,
  CampaignGuard,
  CampaignsGuard,
  CompetitorComparisonGuard,
  CompetitorsGuard,
  LocationsGuard,
  ReviewGuard,
  ReviewSettingsGuard,
  ReviewStatsGuard,
  ReviewsListGuard,
  ReviewTrendsGuard,
} from "./schemas";

// ---------------------------------------------------------------------------
// Reviews
// ---------------------------------------------------------------------------

export function useReviews(
  params: {
    org_id?: string;
    page?: number;
    page_size?: number;
    platform?: string;
    min_rating?: number;
    max_rating?: number;
    reply_status?: string;
    location_id?: number;
  } = {}
) {
  const { page = 1, page_size = 25, ...rest } = params;
  const offset = (page - 1) * page_size;
  return useApiQuery<ReviewsListResponse>(reviewsKeys.list("reviews", params), "/api/reviews", {
    axiosConfig: { params: { ...rest, offset, limit: page_size } },
    schema: asResponseGuard<ReviewsListResponse>(ReviewsListGuard),
  });
}

export function useReview(id: number | null) {
  return useApiQuery<Review>(reviewsKeys.detail("reviews", id ?? 0), `/api/reviews/${id}`, {
    enabled: id !== null,
    schema: asResponseGuard<Review>(ReviewGuard),
  });
}

export function useReviewStats(orgId?: string) {
  return useApiQuery<ReviewStats>(reviewsKeys.list("review-stats", { orgId }), "/api/reviews/stats", {
    axiosConfig: orgId ? { params: { org_id: orgId } } : undefined,
    schema: asResponseGuard<ReviewStats>(ReviewStatsGuard),
  });
}

export function useReviewTrends(orgId?: string, months = 6) {
  // Backend GET /api/reviews/trends accepts `period` (daily|weekly|monthly) and
  // `months` (1..24). A previously-sent `days` param was silently ignored, so
  // the endpoint always returned its default 6-monthly buckets while the UI
  // mislabeled them "30 days". Send the real contract params.
  return useApiQuery<ReviewTrends>(reviewsKeys.list("review-trends", { orgId, months }), "/api/reviews/trends", {
    axiosConfig: { params: { ...(orgId ? { org_id: orgId } : {}), period: "monthly", months } },
    schema: asResponseGuard<ReviewTrends>(ReviewTrendsGuard),
  });
}

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

export function useCampaigns(orgId?: string) {
  return useApiQuery<ReviewCampaignsResponse>(reviewsKeys.list("campaigns", { orgId }), "/api/campaigns", {
    axiosConfig: orgId ? { params: { org_id: orgId } } : undefined,
    schema: asResponseGuard<ReviewCampaignsResponse>(CampaignsGuard),
  });
}

export function useCampaign(id: number | null) {
  return useApiQuery<ReviewCampaign>(reviewsKeys.detail("campaigns", id ?? 0), `/api/campaigns/${id}`, {
    enabled: id !== null,
    schema: asResponseGuard<ReviewCampaign>(CampaignGuard),
  });
}

// ---------------------------------------------------------------------------
// Competitors
// ---------------------------------------------------------------------------

export function useCompetitors(orgId?: string) {
  return useApiQuery<CompetitorsResponse>(reviewsKeys.list("competitors", { orgId }), "/api/competitors", {
    axiosConfig: orgId ? { params: { org_id: orgId } } : undefined,
    schema: asResponseGuard<CompetitorsResponse>(CompetitorsGuard),
  });
}

export function useCompetitorComparison(orgId?: string) {
  return useApiQuery<CompetitorComparison>(
    reviewsKeys.list("competitor-comparison", { orgId }),
    "/api/competitors/comparison",
    {
      axiosConfig: orgId ? { params: { org_id: orgId } } : undefined,
      schema: asResponseGuard<CompetitorComparison>(CompetitorComparisonGuard),
    }
  );
}

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

export function useAlerts(orgId?: string, acknowledged?: boolean) {
  return useApiQuery<ReviewAlertsResponse>(reviewsKeys.list("alerts", { orgId, acknowledged }), "/api/alerts", {
    axiosConfig: {
      params: {
        ...(orgId ? { org_id: orgId } : {}),
        ...(acknowledged !== undefined ? { acknowledged } : {}),
      },
    },
    schema: asResponseGuard<ReviewAlertsResponse>(AlertsGuard),
  });
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export function useReviewSettings(orgId?: string) {
  return useApiQuery<ReviewSettings>(reviewsKeys.list("settings", { orgId }), "/api/settings", {
    axiosConfig: orgId ? { params: { org_id: orgId } } : undefined,
    schema: asResponseGuard<ReviewSettings>(ReviewSettingsGuard),
  });
}

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

export function useLocations(orgId?: string) {
  return useApiQuery<LocationsResponse>(reviewsKeys.list("locations", { orgId }), "/api/locations", {
    axiosConfig: orgId ? { params: { org_id: orgId } } : undefined,
    schema: asResponseGuard<LocationsResponse>(LocationsGuard),
  });
}

// ---------------------------------------------------------------------------
// Businesses (agency-wide switcher options)
// ---------------------------------------------------------------------------

/**
 * All businesses this agency manages — feeds the header business switcher.
 * Unlike {@link useLocations} (scoped to the current org), this lists every
 * `business_locations` row across org slugs. Not affected by the selected-org
 * X-Tenant-Id, so it stays stable while switching.
 */
export function useBusinesses() {
  return useApiQuery<BusinessesResponse>(reviewsKeys.list("businesses"), "/api/businesses", {
    schema: asResponseGuard<BusinessesResponse>(BusinessesGuard),
  });
}
